import {
  approveConfigDraft,
  createConfigDraft,
  fetchControlPlaneState,
  publishConfigDraft,
  validateConfigDraft,
} from "../runtime-config/client";
import type { ControlPlanePayload } from "../runtime-config/contracts";

export const H3_POLICY_KEY = "composer.i2v.h3.jobs.policy.v1";
export type H3Limits = {
  submission_concurrency: number;
  account_outstanding_limit: number;
  maximum_generation_seconds: number;
};
export type H3Policy = H3Limits &
  Record<string, unknown> & {
    minimum_generation_seconds: number;
    allowed_destinations: string[];
  };

export function h3Policy(payload: ControlPlanePayload): H3Policy | null {
  const value = payload.active_release?.values[H3_POLICY_KEY];
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const policy = value as H3Policy;
  if (
    policy.schema_version !== "h3-job-policy.v1" ||
    !Array.isArray(policy.allowed_destinations) ||
    !policy.allowed_destinations.every((item) => typeof item === "string") ||
    ![
      "submission_concurrency",
      "account_outstanding_limit",
      "minimum_generation_seconds",
      "maximum_generation_seconds",
    ].every((key) => Number.isInteger(policy[key]))
  )
    return null;
  return policy;
}

export function h3ReaderStatus(payload: ControlPlanePayload, now = Date.now()) {
  return (["orchestrator", "composer"] as const).map((service) => ({
    service,
    reported: payload.readbacks.some((row) => {
      const stamp = Date.parse(row.updated_at ?? row.applied_at ?? "");
      return (
        row.environment === payload.environment &&
        row.service === service &&
        row.release_id === payload.active_release?.id &&
        row.digest === payload.active_release?.digest &&
        ["valid", "applied"].includes(row.validation_result) &&
        Array.isArray(row.provider_experiment_contracts) &&
        ["h3-job-policy.v1", "h3-reference-only.v1"].every((capability) =>
          row.provider_experiment_contracts?.includes(capability),
        ) &&
        Number.isFinite(stamp) &&
        now >= stamp &&
        now - stamp <= 3_600_000
      );
    }),
  }));
}

export function previewH3Limits(
  payload: ControlPlanePayload,
  limits: H3Limits,
) {
  const policy = h3Policy(payload);
  if (
    payload.environment !== "staging" ||
    !payload.active_release ||
    !policy ||
    policy.allowed_destinations.join(",") !== "fal"
  ) {
    throw new Error(
      "These controls require a published fal-only policy in engineering staging (staging).",
    );
  }
  if (payload.active_release.rules.some((rule) => rule.key === H3_POLICY_KEY)) {
    throw new Error(
      "Scoped H3 policies require a reviewed control-plane release.",
    );
  }
  const bounds = {
    submission_concurrency: [1, 16],
    account_outstanding_limit: [1, 40],
    maximum_generation_seconds: [policy.minimum_generation_seconds, 15],
  };
  for (const key of Object.keys(bounds) as (keyof H3Limits)[]) {
    const [minimum, maximum] = bounds[key];
    if (
      !Number.isInteger(limits[key]) ||
      limits[key] < minimum ||
      limits[key] > maximum
    ) {
      throw new Error(
        `Invalid ${key}: expected an integer from ${minimum} to ${maximum}.`,
      );
    }
  }
  // Explicit fields prevent a caller from smuggling routes, credentials or
  // other operational changes through the limits control.
  return {
    ...policy,
    submission_concurrency: limits.submission_concurrency,
    account_outstanding_limit: limits.account_outstanding_limit,
    maximum_generation_seconds: limits.maximum_generation_seconds,
  };
}

export async function publishH3Limits(
  reviewed: ControlPlanePayload,
  limits: H3Limits,
  reason: string,
) {
  if (reason.trim().length < 10)
    throw new Error("Enter a reason of at least 10 characters.");
  const nextPolicy = previewH3Limits(reviewed, limits);
  const current = await fetchControlPlaneState();
  if (current.status !== "ok")
    throw new Error("Cannot verify the current control-plane state.");
  if (
    current.payload.environment !== reviewed.environment ||
    current.payload.active_release?.id !== reviewed.active_release?.id ||
    current.payload.active_release?.digest !== reviewed.active_release?.digest
  ) {
    throw new Error(
      "The environment or release changed. Refresh and review the new values.",
    );
  }
  const active = current.payload.active_release!;
  let draft = await createConfigDraft({
    base_release_id: active.id,
    values: { ...active.values, [H3_POLICY_KEY]: nextPolicy },
    rules: active.rules,
    reason: reason.trim(),
    ticket_reference: "H3F-12",
  });
  draft = await validateConfigDraft(draft);
  draft = await approveConfigDraft(draft);
  return publishConfigDraft(draft);
}
